import { type IconKey } from '@/content/site';
import { type Accessory, type AccessoryCategory } from '@/services/accessories/types';

/**
 * One shelf, ready to draw.
 *
 * The page's own shape rather than either side's: `label`, `blurb` and `count`
 * come from the catalogue's facets (the database is the only thing that knows
 * what a shelf an administrator added is called, or how many parts are on it),
 * and `icon` comes from `content/accessories.ts`, which is the only thing that
 * knows what it should look like. `useAccessoriesPage` is where the two meet.
 */
export type ShelfTile = {
  value: AccessoryCategory;
  label: string;
  /** Empty rather than null — a tile omits the line, it does not print "null". */
  blurb: string;
  icon: IconKey;
  count: number;
};

/** Where an accessory lives. Stated once, so no call site builds it by hand. */
export const accessoryPath = (a: Pick<Accessory, 'slug'>): string => `/accessories/${a.slug}`;

/**
 * What WhatsApp opens with when the question is about this part.
 *
 * It names the part and asks the fitment question, because the fitment
 * question is the one the visitor was about to type anyway and the one our
 * side needs answered before we can quote. A message that only says "I am
 * interested in the alloy wheels" starts a conversation two turns behind where
 * this one starts.
 *
 * Kept beside `accessoryPath` rather than in `utils/whatsapp.ts`: that module
 * builds the URL, and this decides what the sentence says. Only one of those
 * two is a product decision.
 */
export function accessoryEnquiryMessage(a: Pick<Accessory, 'name' | 'brand'>): string {
  return `Hello Kabex Motors, I am interested in the ${a.name} (${a.brand}). Will it fit my car?`;
}
